import { omit } from 'lodash'

export const reduceMultiRoomSearch = (responses, rooms) => {
  const flatResponses = responses
    .map((res, index) =>
      reduceAccommodations(res?.data?.['hydra:member'])?.map(accommodation => ({
        ...accommodation,
        roomIndex: index,
      })),
    )
    .flat()

  const mergedResponses = flatResponses.reduce((obj, accommodation) => {
    const { id } = accommodation
    const roomPrices = obj[id]?.roomPrices || []
    const priceFrom = obj[id]?.priceFrom || null
    roomPrices[accommodation.roomIndex] = accommodation.price
    const availableRooms = obj[id]?.rooms || []
    availableRooms[accommodation.roomIndex] = accommodation.rooms || []
    return {
      ...obj,
      [id]: {
        ...omit(accommodation, ['price', 'roomIndex']),
        priceFrom:
          !priceFrom || accommodation.priceFrom < priceFrom
            ? accommodation.priceFrom
            : priceFrom,
        roomPrices,
        rooms: availableRooms,
      },
    }
  }, {})

  return Object.values(mergedResponses).filter(accommodation => {
    const availableRooms = accommodation?.rooms?.reduce((obj, group) => {
      group.forEach(({ numberOfUnits, roomTypeCode }) => {
        if (!obj[roomTypeCode] || obj[roomTypeCode] < +numberOfUnits) {
          // eslint-disable-next-line no-param-reassign
          obj[roomTypeCode] = +numberOfUnits
        }
      })

      return obj
    }, {})

    const hasUnitsAvailable =
      Object.keys(availableRooms).length >= rooms.length ||
      Object.values(availableRooms).some(
        numberOfUnits => numberOfUnits >= rooms.length,
      )

    return (
      hasUnitsAvailable &&
      accommodation.roomPrices?.filter(price => !!price)?.length ===
        rooms?.length
    )
  })
}

const reduceAccommodations = (accommodations, index) =>
  accommodations.map((accommodation, accommodationIndex) => {
    const propertyInfo = accommodation?.basicPropertyInfo
    const address = propertyInfo?.address

    const prices = accommodation?.price?.length
      ? accommodation.price.map(price => price.amountBeforeTax)
      : []

    const priceFrom = prices?.length ? Math.min(...prices) : undefined

    const uniqueResources = formatHotelResources(accommodation?.roomTypes)

    const searchTerms = [
      propertyInfo?.hotelName || '',
      address?.cityName || '',
      address?.region || '',
      address?.brandName || '',
      address?.countryName || '',
      address?.stateProv || '',
      propertyInfo?.roomDescription?.name || '',
      propertyInfo?.roomDescription?.text || '',
    ]

    if (propertyInfo?.viewExtensions?.type?.length) {
      propertyInfo.viewExtensions.type.forEach(type => searchTerms.push(type))
    }

    return {
      ...accommodation,
      id:
        propertyInfo.hotelCode ||
        propertyInfo?.hotelName?.replaceAll(' ', '_') ||
        index,
      longDescription: propertyInfo?.viewExtensions?.description?.long,
      amenities: propertyInfo?.viewExtensions?.amenities,
      awards: propertyInfo?.viewExtensions?.awards,
      image: propertyInfo?.viewExtensions?.image,
      hasCapacity: uniqueResources.length > 0,
      name: propertyInfo?.hotelName,
      cityName: address?.cityName,
      brandCode: propertyInfo?.brandCode,
      priceFrom,
      directions: propertyInfo?.viewExtensions?.directions,
      images: propertyInfo?.viewExtensions?.media || [],
      isFeatured: false, // no field for this yet
      longitude: Number(address?.viewExtension?.position.longitude),
      latitude: Number(address?.viewExtension?.position.latitude),
      searchTerms,
      resources: uniqueResources,
      ticketInfos: propertyInfo?.viewExtensions?.policies || [],
      type: propertyInfo?.viewExtensions?.type || [],
      initialOrder: accommodationIndex,
      targetGroups: propertyInfo?.viewExtensions?.audience,
      slug: accommodation?.viewExtensions?.slug,
      rooms: accommodation?.viewExtensions?.rooms || [],
      ribbon:
        propertyInfo?.viewExtensions?.ribbon &&
        !!propertyInfo?.viewExtensions?.ribbon?.enabled
          ? {
              ...(propertyInfo?.viewExtensions?.ribbon || {}),
              bg: propertyInfo?.viewExtensions?.ribbon?.colour || '#cd1919',
            }
          : null,
    }
  })

export const formatHotelResources = roomTypes => {
  const formattedResources = []
  roomTypes?.forEach(roomType => {
    roomType?.viewExtensions?.roomRates.forEach(roomRate => {
      const name =
        roomRate?.viewExtensions?.roomRateDescription?.name ||
        roomType?.roomDescription?.name
      formattedResources.push({
        id: roomType?.roomTypeCode,
        ratePlanCode: roomRate.ratePlanCode,
        roomBookitResourceId: roomType?.bookingMatrix?.[0]?.bookitResourceId,
        bookitResourceId:
          roomRate?.viewExtensions?.bookingMatrix?.[0]?.bookitResourceId,
        campingPackage:
          !!roomRate?.viewExtensions?.bookingMatrix?.[0]?.campingPackage,
        bookitProductId:
          roomRate?.viewExtensions?.bookingMatrix?.[0]?.bookitProductId,
        bookitSupplierId:
          roomRate?.viewExtensions?.bookingMatrix?.[0]?.bookitSupplierId,
        capacity: roomType.occupancy?.maxOccupancy,
        price: roomRate?.rates?.[0]?.total?.amountAfterTax,
        name: name?.trim() || '',
        text:
          roomRate?.viewExtensions?.roomRateDescription?.text ||
          roomType?.roomDescription?.text,
        roomClassificationCode: roomType?.roomClassificationCode,
        checkInDateTime: roomType.viewExtensions?.checkInDateTime,
        checkOutDateTime: roomType.viewExtensions?.checkOutDateTime,
        numberOfUnits: roomRate?.numberOfUnits || 1,
      })
    })
  })
  return formattedResources
}

export default reduceAccommodations
